// Pure data + helper utilities for hero programs

/* =========================
   PROGRAM TEMPLATES
   ========================= */

const PROGRAM_TEMPLATES = {
  /* --- POWERHOUSE: heavy strength & mass --- */
  powerhouse: {
    id: "powerhouse",
    name: "Powerhouse Strength",
    frequency: "4–5 days/week",
    weeks: 9,
    defaultDaysPerWeek: 4,
    description:
      "Heavy compound lifting and high-volume hypertrophy for maximum strength and size. Built for Thor / Hulk / Kratos–tier physiques.",
    focus: ["Heavy compounds", "High volume", "Strength + mass"],
    days: [
      {
        id: "ph_d1",
        label: "Day 1 – Heavy Chest & Back",
        info: "Foundation upper-body strength with enough volume to actually grow. Think armor-plated torso.",
        exercises: [
          {
            name: "Barbell Bench Press",
            setsReps: "5 × 3–5",
            notes:
              "Add weight when all sets are solid and bar speed stays fast.",
          },
          {
            name: "Weighted Pull-Ups",
            setsReps: "4 × 6–8",
            notes:
              "Chest to bar if possible. Full stretch at the bottom. If you can’t do clean reps yet, use band-assisted pull-ups or a lat pulldown machine.",
          },
          {
            name: "Incline DB Bench Press",
            setsReps: "4 × 8–10",
            notes: "Aim at upper chest. Slight pause at the bottom.",
          },
          {
            name: "Chest-Supported Row",
            setsReps: "4 × 8–10",
            notes:
              "Pull elbows toward your hips. Don’t jerk the weight. A T-bar row is a fine alternative if your gym doesn’t have a chest-supported setup.",
          },
          {
            name: "Cable Flyes",
            setsReps: "3 × 12–15",
            notes:
              "Slow stretch, big squeeze. Think mind–muscle connection. Pec deck is okay if cables aren’t available.",
          },
          {
            name: "Straight-Arm Pulldown",
            setsReps: "3 × 12–15",
            notes: "Lock lats in. Don’t bend elbows too much.",
          },
        ],
      },
      {
        id: "ph_d2",
        label: "Day 2 – Heavy Legs & Posterior Chain",
        info: "Squats, hinges, and enough leg volume to actually change the way your jeans fit.",
        exercises: [
          {
            name: "Back Squat",
            setsReps: "5 × 3–5",
            notes: "Depth and control first. Add load only with clean reps.",
          },
          {
            name: "Romanian Deadlift",
            setsReps: "4 × 6–8",
            notes: "Hinge at the hips, feel hamstrings and glutes load.",
          },
          {
            name: "Leg Press",
            setsReps: "4 × 8–12",
            notes: "Controlled tempo. Don’t half-rep just to move more weight.",
          },
          {
            name: "Leg Curl",
            setsReps: "3 × 10–15",
            notes:
              "Slow eccentric. Lying or seated is fine—pick the machine that feels best on your knees and hamstrings.",
          },
          {
            name: "Standing Calf Raise",
            setsReps: "4 × 12–15",
            notes: "Pause in the stretch and at the peak contraction.",
          },
          {
            name: "Hanging Leg Raises",
            setsReps: "3 × 10–15",
            notes:
              "Core tight; no swinging or momentum reps. If needed, regress to ab wheel or controlled knee raises.",
          },
        ],
      },
      {
        id: "ph_d3",
        label: "Day 3 – Power Upper (Shoulders & Arms)",
        info: "Pressing strength plus the delt and arm volume you need to look like a comic panel.",
        exercises: [
          {
            name: "Overhead Press",
            setsReps: "4 × 5–8",
            notes:
              "Brace your core so you’re not turning it into a lower back exercise. Use a barbell or dumbbells depending on shoulder comfort.",
          },
          {
            name: "Incline Bench Press",
            setsReps: "4 × 6–8",
            notes: "Explosive on the way up, controlled on the way down.",
          },
          {
            name: "Weighted Dips",
            setsReps: "3 × 8–10",
            notes:
              "Lean slightly forward if you want more chest, upright for more triceps. If dips bother your shoulders, swap for a chest press machine.",
          },
          {
            name: "Lateral Raises",
            setsReps: "4 × 12–15",
            notes: "Small swing is fine, but control the lowering phase.",
          },
          {
            name: "Barbell / EZ-Bar Curls",
            setsReps: "4 × 8–12",
            notes: "Full stretch, no ego swinging. Let biceps do the work.",
          },
          {
            name: "Cable Triceps Pressdowns",
            setsReps: "4 × 8–12",
            notes:
              "Keep elbows pinned; don’t flare them out. Skullcrushers are an optional replacement if your elbows tolerate them.",
          },
        ],
      },
      {
        id: "ph_d4",
        label: "Day 4 – Heavy Deadlift & Back Offense",
        info: "Deadlifts + back density + core. This is where the superhero silhouette gets built.",
        exercises: [
          {
            name: "Deadlift (Conventional or Sumo)",
            setsReps: "5 × 3",
            notes: "Stay 1–2 reps shy of failure. No ugly grinders.",
          },
          {
            name: "Front Squat",
            setsReps: "4 × 5–8",
            notes:
              "Torso upright, proud chest, braced core. Hack squat is fine if front squats don’t agree with your shoulders/wrists.",
          },
          {
            name: "Walking Lunges",
            setsReps: "3 × 10–12/leg",
            notes: "Long stride, knee tracks over toes, push through heel.",
          },
          {
            name: "Barbell or Machine Row",
            setsReps: "4 × 8–10",
            notes: "Pull to lower ribs. Squeeze shoulder blades together.",
          },
          {
            name: "Reverse Flyes",
            setsReps: "3 × 12–15",
            notes:
              "Control the weight; no swinging. Rear delt machine is fine.",
          },
          {
            name: "Hanging Leg Raises",
            setsReps: "3 × 12–15",
            notes: "Brace abs, don’t just swing your hips. Cable crunch is ok.",
          },
        ],
      },
      {
        id: "ph_d5",
        label: "Day 5 – Arms, Delts & Finisher",
        info: "Finisher day for shoulder caps, arm thickness, and a little pump conditioning.",
        exercises: [
          {
            name: "Seated DB Shoulder Press",
            setsReps: "4 × 8–10",
            notes: "No bouncing out of the bottom; smooth and controlled.",
          },
          {
            name: "Lateral Raises",
            setsReps: "4 × 12–15",
            notes: "Chase the burn here. Short rest is fine.",
          },
          {
            name: "Rear Delt Flyes",
            setsReps: "3 × 12–15",
            notes: "Think about pulling pinkies back, not just flapping arms.",
          },
          {
            name: "Barbell / DB Curls (Heavy)",
            setsReps: "3 × 6–10",
            notes: "Heavier weight, still under control.",
          },
          {
            name: "Cable Curls",
            setsReps: "3 × 10–12",
            notes:
              "Squeeze hard at the top. Use a straight bar, EZ bar, or rope—whatever keeps your wrists happy.",
          },
          {
            name: "Overhead Triceps Extensions",
            setsReps: "3 × 10–12",
            notes: "Stretch at the bottom; don’t slam elbows.",
          },
          {
            name: "Optional: Sled Push / Battle Ropes",
            setsReps: "5–10 min",
            notes: "Short, hard bursts as a conditioning/pump finisher.",
          },
        ],
      },
    ],
  },

  /* --- SUPER SOLDIER: hybrid strength & conditioning --- */
  super_soldier: {
    id: "super_soldier",
    name: "Super Soldier Athletics",
    frequency: "4–5 days/week",
    weeks: 9,
    defaultDaysPerWeek: 4,
    description:
      "Balanced strength, muscle, and conditioning. Peak human build like Captain America / Batman / Geralt / Master Chief.",
    focus: ["Hybrid strength", "Conditioning", "Functional muscle"],
    days: [
      {
        id: "ss_d1",
        label: "Day 1 – Upper Strength & Volume",
        info: "Push–pull strength plus enough volume to look jacked in and out of the suit.",
        exercises: [
          {
            name: "Weighted Pull-Ups",
            setsReps: "4 × 5–8",
            notes:
              "Add weight when you can hit all sets on the high end. Use bands or assistance if you’re not there yet.",
          },
          {
            name: "Barbell Bench Press",
            setsReps: "4 × 6–8",
            notes:
              "Slight pause at the bottom; control the descent. Dumbbells are fine if your shoulders prefer them.",
          },
          {
            name: "One-Arm DB Row",
            setsReps: "4 × 8–10/arm",
            notes: "Elbow tracks along body, not flaring way out.",
          },
          {
            name: "Standing DB Shoulder Press",
            setsReps: "3 × 8–10",
            notes: "Brace your core; no overextended lower back.",
          },
          {
            name: "Face Pulls",
            setsReps: "3 × 12–15",
            notes: "Elbows high, squeeze upper back and rear delts.",
          },
          {
            name: "Barbell or DB Curls",
            setsReps: "3 × 10–12",
            notes: "Smooth reps; don’t turn it into a hip thrust.",
          },
        ],
      },
      {
        id: "ss_d2",
        label: "Day 2 – Lower Strength + Conditioning",
        info: "Leg strength plus conditioning so you can sprint, jump, and fight without gassing out.",
        exercises: [
          {
            name: "Back Squat",
            setsReps: "4 × 5–7",
            notes:
              "Explosive up, controlled down. Depth > ego. Front squats are fine if they feel better on your back.",
          },
          {
            name: "Romanian Deadlift",
            setsReps: "3 × 8–10",
            notes: "Hinge, not squat. Feel hamstrings lengthen.",
          },
          {
            name: "Reverse Lunges",
            setsReps: "3 × 8–10/leg",
            notes: "Step back to reduce knee stress, drive through front heel.",
          },
          {
            name: "Leg Curl",
            setsReps: "3 × 8–12",
            notes:
              "Control the lowering; don’t drop. Nordic curls are a brutal advanced option if you have the setup.",
          },
          {
            name: "Sled Push or Bike/Row Sprints",
            setsReps: "8 × 15–20s",
            notes:
              "Hard effort, full recovery. Think combat rounds, not jogging.",
          },
        ],
      },
      {
        id: "ss_d3",
        label: "Day 3 – Upper Volume & Core",
        info: "Higher-volume upper work with core so you can fight, climb, and move like a super soldier.",
        exercises: [
          {
            name: "Incline DB Press",
            setsReps: "4 × 8–10",
            notes: "Drive through chest and shoulders; don’t bounce.",
          },
          {
            name: "Chin-Ups (Band Assisted if needed)",
            setsReps: "4 × AMRAP",
            notes: "Full stretch, chest toward bar at the top.",
          },
          {
            name: "Seated Cable Row or Chest-Supported Row",
            setsReps: "3 × 10–12",
            notes: "Control tempo; don’t just ‘throw’ weight.",
          },
          {
            name: "DB Lateral Raises",
            setsReps: "3 × 12–15",
            notes: "Raise to just under shoulder height; no shrugging.",
          },
          {
            name: "Hanging Leg Raises or Toe-to-Bar",
            setsReps: "3 × 10–15",
            notes: "Slow, controlled, no swinging kips.",
          },
          {
            name: "Plank Variations",
            setsReps: "3 × 30–45s",
            notes: "Stay tight; don’t let hips sag.",
          },
        ],
      },
      {
        id: "ss_d4",
        label: "Day 4 – Athletic Day (Speed & Power)",
        info: "Sprints, jumps, and circuits so you function like a hero, not just look like one.",
        exercises: [
          {
            name: "Box Jumps",
            setsReps: "4 × 3–5",
            notes:
              "Full recovery between sets; crisp landings. Broad jumps work too if you don’t have a safe box.",
          },
          {
            name: "Kettlebell Swings",
            setsReps: "4 × 12–15",
            notes: "Hip drive, not a front raise. Snappy reps.",
          },
          {
            name: "Push-Up Variations (Deficit or Feet Elevated)",
            setsReps: "4 × 10–20",
            notes: "Pick a version that challenges you at those reps.",
          },
          {
            name: "Farmer’s Carries",
            setsReps: "4 × 30–40m",
            notes: "Walk tall, grip tight. No waddling.",
          },
          {
            name: "Short Conditioning Circuit",
            setsReps: "10–15 min",
            notes: "2–3 moves (e.g., rower, burpees, core). Keep rest short.",
          },
        ],
      },
      {
        id: "ss_d5",
        label: "Day 5 – Conditioning & Arms",
        info: "Engine + aesthetics—heart and lungs plus heroic arms and shoulders.",
        exercises: [
          {
            name: "Row/Bike/Run Intervals",
            setsReps: "10 × 30s hard / 60–90s easy",
            notes:
              "Pick rower, bike, or treadmill. Push hard, but stay in control.",
          },
          {
            name: "Barbell or DB Curls",
            setsReps: "4 × 8–12",
            notes: "No heaving. Squeeze at the top.",
          },
          {
            name: "Skullcrushers or Cable Overhead Extensions",
            setsReps: "4 × 8–12",
            notes: "Stretch, then drive up. Protect elbows.",
          },
          {
            name: "Hammer Curls",
            setsReps: "3 × 10–12",
            notes: "Great for forearms and brachialis to round out the arm.",
          },
          {
            name: "Core Finisher (Heavy Carries / Russian Twists etc.)",
            setsReps: "3–4 rounds",
            notes:
              "Mix carries with rotational core for real-world fighting strength.",
          },
        ],
      },
    ],
  },

  /* --- AGILE HERO: bodyweight, agility & mobility --- */
  agile_hero: {
    id: "agile_hero",
    name: "Agile Hero Physique",
    frequency: "4–5 days/week",
    weeks: 9,
    defaultDaysPerWeek: 4,
    description:
      "Lean, explosive, mobile physique. Perfect for Spider-Man / Flash / Levi / Black Panther style movement.",
    focus: ["Bodyweight strength", "Agility", "Mobility"],
    days: [
      {
        id: "ag_d1",
        label: "Day 1 – Push Calisthenics & Shoulders",
        info: "Gymnastic-style pushing with shoulder work so you can launch, swing, and strike.",
        exercises: [
          {
            name: "Ring or Bar Dips",
            setsReps: "4 × 6–10",
            notes:
              "Full depth, stable shoulders, lock out hard. Use bars if rings feel too unstable.",
          },
          {
            name: "Push-Ups (Weighted or Deficit)",
            setsReps: "4 × 10–15",
            notes: "Keep body in a straight line. No sagging hips.",
          },
          {
            name: "Pike Push-Ups / Handstand Push-Up Progressions",
            setsReps: "4 × 6–8",
            notes: "Control lowering; build toward strict handstand push-ups.",
          },
          {
            name: "DB Lateral Raises",
            setsReps: "3 × 12–15",
            notes: "Raise with control; keep tension on delts, not traps.",
          },
          {
            name: "Diamond or Close-Grip Push-Ups",
            setsReps: "3 × 12–20",
            notes: "Hit triceps and inner chest as a finisher.",
          },
        ],
      },
      {
        id: "ag_d2",
        label: "Day 2 – Pull, Grip & Upper Back",
        info: "Pulling strength, grip, and mid-back to support swinging, climbing, and grappling.",
        exercises: [
          {
            name: "Pull-Ups (Various Grips)",
            setsReps: "5 × 5–8",
            notes:
              "Rotate grips weekly (wide, neutral, close). Full ROM always.",
          },
          {
            name: "Inverted Rows (Feet Elevated if strong)",
            setsReps: "4 × 8–12",
            notes: "Keep core tight, chest to bar/edge.",
          },
          {
            name: "Single-Arm DB Row",
            setsReps: "3 × 10–12/arm",
            notes: "Stretch at bottom, squeeze at top.",
          },
          {
            name: "Biceps Curls (Barbell or Rings)",
            setsReps: "3 × 10–12",
            notes: "Full stretch on every rep.",
          },
          {
            name: "Dead Hangs / Grip Holds",
            setsReps: "3–4 × 20–40s",
            notes: "Develop grip for climbing and weapons handling.",
          },
        ],
      },
      {
        id: "ag_d3",
        label: "Day 3 – Legs, Plyometrics & Mobility",
        info: "Strong but agile legs that can cut, jump, and land safely.",
        exercises: [
          {
            name: "Back Squat",
            setsReps: "4 × 6–8",
            notes: "Use a load you can control to full depth.",
          },
          {
            name: "Bulgarian Split Squats",
            setsReps: "3 × 8–12/leg",
            notes:
              "Find balance; don’t rush. These build real-world leg power.",
          },
          {
            name: "Romanian Deadlifts or Single-Leg RDLs",
            setsReps: "3 × 8–10",
            notes: "Hamstrings + balance. Move slow here.",
          },
          {
            name: "Box Jumps / Tuck Jumps",
            setsReps: "4 × 3–5",
            notes: "Crisp landings on soft knees.",
          },
          {
            name: "Hip & Ankle Mobility Flow",
            setsReps: "10–15 min",
            notes:
              "Deep squat holds, Cossack squats, calf stretches. Your joints are your asset.",
          },
        ],
      },
      {
        id: "ag_d4",
        label: "Day 4 – Conditioning, Core & Agility",
        info: "Sprints, core and footwork so you move like a hero in real space, not just on paper.",
        exercises: [
          {
            name: "Short Sprints (40–60m)",
            setsReps: "8–10 × 40–60m",
            notes: "Walk back as rest. Stay crisp; no sloppy reps.",
          },
          {
            name: "Hanging Leg Raises / Windshield Wipers",
            setsReps: "4 × 10–15",
            notes: "Strict control, no wild swinging.",
          },
          {
            name: "Agility / Ladder Drills",
            setsReps: "15–20 min",
            notes: "Emphasize quick feet and body control.",
          },
          {
            name: "Core Stability Circuit",
            setsReps: "3–4 rounds",
            notes:
              "Mix side planks, anti-rotation presses (Pallof) and single-leg balance.",
          },
        ],
      },
      {
        id: "ag_d5",
        label: "Day 5 – Hybrid Movement Day",
        info: "Extra conditioning and mixed-modality movement to keep you fast, lean, and dangerous.",
        exercises: [
          {
            name: "Tempo Runs or Shuttle Sprints",
            setsReps: "8–10 × 60–100m",
            notes: "Controlled hard pace, walk back recovery.",
          },
          {
            name: "Circuit: Push-Ups, Air Squats, Sit-Ups",
            setsReps: "3–5 rounds",
            notes:
              "Choose a rep scheme (e.g., 15/20/20) and move with clean form.",
          },
          {
            name: "Jump Rope",
            setsReps: "10–15 min",
            notes: "Stay light on your feet; no heavy stomping.",
          },
          {
            name: "Extended Mobility & Stretching",
            setsReps: "10–15 min",
            notes:
              "Prioritize hips, hamstrings, shoulders, and thoracic spine.",
          },
        ],
      },
    ],
  },

  /* --- AMAZONIAN: female warrior / glute & leg focus --- */
  amazonian: {
    id: "amazonian",
    name: "Amazonian Warrior",
    frequency: "4–5 days/week",
    weeks: 9,
    defaultDaysPerWeek: 4,
    description:
      "Strong, powerful, athletic build with special emphasis on legs, glutes, shoulders, and core. Think Wonder Woman / Lara Croft.",
    focus: ["Glutes & legs", "Core", "Athletic conditioning"],
    days: [
      {
        id: "am_d1",
        label: "Day 1 – Lower Strength & Glutes",
        info: "Heavy lower-body work with glute emphasis for real power and shape.",
        exercises: [
          {
            name: "Back Squat",
            setsReps: "4 × 5–8",
            notes: "Full depth with control. Zero ego lifting.",
          },
          {
            name: "Romanian Deadlifts",
            setsReps: "4 × 8–10",
            notes: "Hips back, soft knees, feel hamstrings and glutes.",
          },
          {
            name: "Walking Lunges",
            setsReps: "3 × 12–15/leg",
            notes: "Long steps, drive through heel, stay upright.",
          },
          {
            name: "Leg Press (Glute-Biased Foot Position)",
            setsReps: "3 × 10–12",
            notes: "Feet higher on the pad, push through heels.",
          },
          {
            name: "Standing or Seated Calf Raises",
            setsReps: "4 × 12–15",
            notes: "Controlled reps; don’t just bounce.",
          },
        ],
      },
      {
        id: "am_d2",
        label: "Day 2 – Upper Body & Core",
        info: "Shoulder, back, chest and core work to build a powerful upper-body silhouette.",
        exercises: [
          {
            name: "Incline DB Press",
            setsReps: "4 × 8–10",
            notes: "Angle that targets upper chest without hurting shoulders.",
          },
          {
            name: "Lat Pulldown / Pull-Ups",
            setsReps: "4 × 8–10",
            notes: "Pull elbows into your side; don’t just shrug.",
          },
          {
            name: "DB Shoulder Press",
            setsReps: "3 × 8–10",
            notes: "Keep wrists stacked over elbows.",
          },
          {
            name: "Cable Row",
            setsReps: "3 × 10–12",
            notes: "Neutral spine, squeeze shoulder blades together.",
          },
          {
            name: "Cable Woodchops",
            setsReps: "3 × 12–15/side",
            notes: "Rotate through torso, not just arms.",
          },
          {
            name: "Plank Variations",
            setsReps: "3 × 30–45s",
            notes: "Braced glutes and ribs down.",
          },
        ],
      },
      {
        id: "am_d3",
        label: "Day 3 – Glute Focus & Hamstrings",
        info: "This is your glute-specialist day: hip thrusts, hinges, and isolation.",
        exercises: [
          {
            name: "Hip Thrusts (Barbell or Machine)",
            setsReps: "4 × 8–12",
            notes: "Lock out and pause; squeeze glutes hard.",
          },
          {
            name: "Good Mornings",
            setsReps: "3 × 8–10",
            notes:
              "Light–moderate weight; slow and controlled. Hips back, neutral spine.",
          },
          {
            name: "Cable Kickbacks",
            setsReps: "3 × 15–20/leg",
            notes: "Squeeze at the top, don’t rush.",
          },
          {
            name: "Leg Curl",
            setsReps: "3 × 10–15",
            notes: "Smooth tempo; hamstrings do the work.",
          },
          {
            name: "Abduction Machine / Band Walks",
            setsReps: "3 × 20–25",
            notes: "Constant tension on the side glutes.",
          },
        ],
      },
      {
        id: "am_d4",
        label: "Day 4 – Conditioning & Athleticism",
        info: "Cardio, athletic drills, and core to keep you moving like a warrior, not just lifting like one.",
        exercises: [
          {
            name: "Bike Sprints",
            setsReps: "8–10 × 20–30s",
            notes:
              "60–90s easy between efforts. Push but stay crisp. Row sprints are fine if you prefer.",
          },
          {
            name: "Goblet Squats",
            setsReps: "3 × 12–15",
            notes: "Use this as a controlled, lighter squat pattern.",
          },
          {
            name: "Walking Lunges",
            setsReps: "3 × 10–12/leg",
            notes: "Bodyweight or light load; move cleanly.",
          },
          {
            name: "Core Circuit (Planks, Dead Bugs, Side Planks)",
            setsReps: "3–4 rounds",
            notes: "Minimal rest; own each position.",
          },
        ],
      },
      {
        id: "am_d5",
        label: "Day 5 – Glute & Core Finisher",
        info: "Dial in glutes and midsection to finish the weekly volume for shape and stability.",
        exercises: [
          {
            name: "Cable/Band Hip Abductions",
            setsReps: "4 × 15–20",
            notes: "Stay in the burn. Slow tempo.",
          },
          {
            name: "Back Extensions (Glute Focus)",
            setsReps: "4 × 12–15",
            notes: "Push hips into pad, squeeze glutes to raise torso.",
          },
          {
            name: "Reverse Hyper (if available) or Hip Extensions",
            setsReps: "3 × 12–15",
            notes: "Smooth reps; no wild swinging.",
          },
          {
            name: "Core Mixed (Leg Raises, Cable Crunches, Obliques)",
            setsReps: "3–4 rounds",
            notes:
              "Pick 2–3 moves and rotate. This is the belt that holds everything together.",
          },
        ],
      },
    ],
  },

  /* --- ONE PUNCH: high-rep calisthenics + strength support --- */
  one_punch: {
    id: "one_punch",
    name: "One Punch Hero",
    frequency: "4–5 days/week",
    weeks: 9,
    defaultDaysPerWeek: 4,
    description:
      "Simple on paper, brutal in practice. High-volume calisthenics plus key strength work to build an engine like Saitama.",
    focus: ["High reps", "Conditioning", "Mental toughness"],
    days: [
      {
        id: "op_d1",
        label: "Day 1 – 100s Foundation",
        info: "The iconic 100/day style work, scaled to your current level across the 9 weeks.",
        exercises: [
          {
            name: "Push-Ups",
            setsReps: "Total 100",
            notes: "Break into sets you can manage (e.g. 10×10, 5×20).",
          },
          {
            name: "Bodyweight Squats",
            setsReps: "Total 100",
            notes: "Full depth, steady tempo. Don’t collapse at the bottom.",
          },
          {
            name: "Sit-Ups / Crunches",
            setsReps: "Total 100",
            notes: "Protect your neck and lower back; no jerking.",
          },
          {
            name: "Optional: Easy Run / Walk",
            setsReps: "10–20 min",
            notes: "This is extra movement, not an all-out run.",
          },
        ],
      },
      {
        id: "op_d2",
        label: "Day 2 – Strength Support",
        info: "Heavy lifting to make the high-rep days easier and protect joints long term.",
        exercises: [
          {
            name: "Deadlift",
            setsReps: "4 × 4–6",
            notes: "Stay 1–2 reps away from failure. Perfect form first.",
          },
          {
            name: "Bench Press",
            setsReps: "4 × 6–8",
            notes:
              "Solid bar path and shoulders packed. If dips suit you better, you can swap occasionally.",
          },
          {
            name: "Pull-Ups / Chin-Ups",
            setsReps: "4 × AMRAP",
            notes: "Use bands if you need them. Full ROM always.",
          },
          {
            name: "Overhead Press or Push Press",
            setsReps: "3 × 6–8",
            notes: "Controlled, powerful reps.",
          },
        ],
      },
      {
        id: "op_d3",
        label: "Day 3 – Conditioning Circuit",
        info: "Higher-volume circuits to build durability and mental toughness.",
        exercises: [
          {
            name: "Push-Up Variations",
            setsReps: "5 × 20",
            notes: "Incline/standard/decline—mix it up if needed.",
          },
          {
            name: "Walking Lunges",
            setsReps: "4 × 15/leg",
            notes: "Bodyweight or light DB for extra challenge.",
          },
          {
            name: "Jump Rope",
            setsReps: "10–15 min",
            notes: "Steady pace. Count total jumps if you like metrics.",
          },
          {
            name: "Core Circuit (Planks, Leg Raises, Crunches)",
            setsReps: "3–4 rounds",
            notes: "Minimal rest; control every rep.",
          },
        ],
      },
      {
        id: "op_d4",
        label: "Day 4 – Run + Mobility",
        info: "Simple cardio and mobility work to keep you healthy enough to keep doing the crazy stuff.",
        exercises: [
          {
            name: "Jog / Easy Run",
            setsReps: "20–40 min",
            notes: "Comfortable conversational pace. This is not a sprint day.",
          },
          {
            name: "Full-Body Mobility (Hips, Ankles, Shoulders)",
            setsReps: "10–15 min",
            notes: "Think long-term durability, not just stretching.",
          },
          {
            name: "Light Core (Bird Dogs, Dead Bugs)",
            setsReps: "3 × 10–15",
            notes: "Slow, deliberate, keep spine neutral.",
          },
        ],
      },
      {
        id: "op_d5",
        label: "Day 5 – Strength & Core Finisher",
        info: "Extra strength plus strong trunk work to make all the high-volume days safer and more effective.",
        exercises: [
          {
            name: "Front Squat",
            setsReps: "4 × 6–8",
            notes:
              "Stay upright; don’t collapse forward. Goblet squats can be a regression if needed.",
          },
          {
            name: "Row Variation (Barbell, DB, or Cable)",
            setsReps: "4 × 8–10",
            notes: "Squeeze shoulder blades together every rep.",
          },
          {
            name: "Weighted Carries",
            setsReps: "3–4 × 30–40m",
            notes: "Farmer’s carry or suitcase carry. Stable and tall.",
          },
          {
            name: "Core Finisher (Planks / Leg Raises / Anti-Rotation)",
            setsReps: "3–4 rounds",
            notes: "Choose 2 moves and go back-to-back.",
          },
        ],
      },
    ],
  },

  /* ===== NEW ARCHETYPES (implemented via aliases) ===== */

  combat_athlete: null, // → super_soldier template
  speedster: null, // → agile_hero template
  classic_bodybuilder: null, // → powerhouse template

  // Old abstract names – still supported as aliases:
  ppl_hypertrophy: null,
  bro_split: null,
  upper_lower_5: null,
};

/* =========================
       PROGRAM ALIASES
       ========================= */

const PROGRAM_ALIAS = {
  // New archetypes:
  combat_athlete: "super_soldier",
  speedster: "agile_hero",
  classic_bodybuilder: "powerhouse",

  // Legacy aliases:
  ppl_hypertrophy: "classic_bodybuilder",
  bro_split: "classic_bodybuilder",
  upper_lower_5: "combat_athlete",
};

/* Resolve a hero's program type into a real template */
export const resolveProgram = (programType) => {
  const direct = PROGRAM_TEMPLATES[programType];
  if (direct && direct !== null) return direct;

  const aliasKey = PROGRAM_ALIAS[programType];
  if (aliasKey && PROGRAM_TEMPLATES[aliasKey]) {
    return PROGRAM_TEMPLATES[aliasKey];
  }

  // Fallback – should basically never happen
  return PROGRAM_TEMPLATES.powerhouse;
};

/* =========================
       WEEK + DIFFICULTY PROGRESSION
       ========================= */

export const getProgressiveSetsReps = (
  setsReps,
  week,
  difficulty = "intermediate"
) => {
  if (!week || week < 1) week = 1;

  // week 1–3 → level 0, 4–6 → level 1, 7–9+ → level 2
  let baseLevel = Math.min(2, Math.floor((week - 1) / 3));
  let level = baseLevel;

  if (difficulty === "beginner") {
    level = Math.max(0, baseLevel - 1);
  } else if (difficulty === "advanced") {
    level = Math.min(2, baseLevel + 1);
  }

  // Pattern: "5 × 8–10"
  const matchRange = setsReps.match(/^\s*(\d+)\s*[×x]\s*(\d+)[–-](\d+)\s*$/);
  if (matchRange) {
    let sets = parseInt(matchRange[1], 10);
    let minR = parseInt(matchRange[2], 10);
    let maxR = parseInt(matchRange[3], 10);

    if (level >= 1) {
      minR += level;
      maxR += level;
    }
    if (level === 2) {
      sets += 1;
    }
    return `${sets} × ${minR}–${maxR}`;
  }

  // Pattern: "Total 100"
  const matchTotal = setsReps.match(/Total\s+(\d+)/i);
  if (matchTotal) {
    let base = parseInt(matchTotal[1], 10);
    const extra = level * Math.round(base * 0.1); // +10% each level
    return `Total ${base + extra}`;
  }

  // Pattern: "2–3 km" etc.
  const matchKm = setsReps.match(/(\d+(\.\d+)?)\s*[–-]\s*(\d+(\.\d+)?)\s*km/i);
  if (matchKm) {
    let min = parseFloat(matchKm[1]);
    let max = parseFloat(matchKm[3]);
    if (level >= 1) {
      min += 0.5 * level;
      max += 0.5 * level;
    }
    return `${min.toFixed(1)}–${max.toFixed(1)} km`;
  }

  // Default: unchanged
  return setsReps;
};

export const getSetsCountFromPrescription = (setsReps) => {
  if (!setsReps) return 3;
  const m = setsReps.match(/^\s*(\d+)\s*[×x]/);
  if (m) {
    const n = parseInt(m[1], 10);
    if (!Number.isNaN(n) && n > 0 && n <= 10) return n;
  }
  if (/Total\s+\d+/i.test(setsReps)) return 1;
  return 3;
};

/* =========================
       AUTO-PROGRESSION (WEIGHT SUGGESTIONS)
       ========================= */

const isLowerBodyExercise = (name) => {
  const lower = name.toLowerCase();
  return /squat|deadlift|leg press|leg curl|leg extension|lunge|hip thrust|glute|hamstring|calf/.test(
    lower
  );
};

const parseRepRangeFromPrescription = (setsReps) => {
  const m = setsReps.match(/(\d+)\s*[–-]\s*(\d+)/);
  if (!m) return null;
  const minR = parseInt(m[1], 10);
  const maxR = parseInt(m[2], 10);
  if (Number.isNaN(minR) || Number.isNaN(maxR)) return null;
  return { minR, maxR };
};

const getPreviousWeekExerciseStats = (prevWeekLog, dayId, exerciseName) => {
  if (!prevWeekLog) return null;
  const dayLog = prevWeekLog[dayId];
  if (!dayLog) return null;
  const entry = dayLog[exerciseName];
  if (!entry || !Array.isArray(entry.sets)) return null;

  const weights = [];
  const reps = [];
  const rpes = [];

  entry.sets.forEach((s) => {
    const w = parseFloat(s.weight);
    if (!Number.isNaN(w)) weights.push(w);
    const r = parseFloat(s.reps);
    if (!Number.isNaN(r)) reps.push(r);
    const rp = parseFloat(s.rpe);
    if (!Number.isNaN(rp)) rpes.push(rp);
  });

  if (!weights.length) return null;
  const avgWeight = weights.reduce((sum, w) => sum + w, 0) / weights.length;
  const avgRpe =
    rpes.length > 0 ? rpes.reduce((sum, r) => sum + r, 0) / rpes.length : null;

  return { avgWeight, reps, avgRpe };
};

export const getSuggestedWeight = (
  prevWeekLog,
  dayId,
  exerciseName,
  setsReps,
  difficulty
) => {
  const stats = getPreviousWeekExerciseStats(prevWeekLog, dayId, exerciseName);
  if (!stats) return null;

  const { avgWeight, reps, avgRpe } = stats;
  if (!avgWeight || avgWeight <= 0) return null;

  const repRange = parseRepRangeFromPrescription(setsReps);
  const baseInc = isLowerBodyExercise(exerciseName) ? 10 : 5;

  let suggested = avgWeight;

  if (difficulty === "beginner") {
    suggested = avgWeight + baseInc;
  } else if (difficulty === "intermediate") {
    if (repRange && reps.length) {
      const { minR, maxR } = repRange;
      const allAtTop = reps.every((r) => r >= maxR);
      const mostlyBelowMin =
        reps.filter((r) => r < minR).length > reps.length / 2;

      if (allAtTop) {
        suggested = avgWeight + baseInc;
      } else if (mostlyBelowMin) {
        suggested = avgWeight - baseInc;
      } else {
        suggested = avgWeight;
      }
    }
  } else if (difficulty === "advanced") {
    if (avgRpe && !Number.isNaN(avgRpe)) {
      if (avgRpe <= 7) {
        suggested = avgWeight + baseInc;
      } else if (avgRpe > 8.5) {
        suggested = avgWeight - baseInc;
      } else {
        suggested = avgWeight;
      }
    } else if (repRange && reps.length) {
      const { minR, maxR } = repRange;
      const allAtTop = reps.every((r) => r >= maxR);
      const mostlyBelowMin =
        reps.filter((r) => r < minR).length > reps.length / 2;

      if (allAtTop) {
        suggested = avgWeight + baseInc;
      } else if (mostlyBelowMin) {
        suggested = avgWeight - baseInc;
      } else {
        suggested = avgWeight;
      }
    }
  }

  if (!suggested || suggested <= 0) return null;

  const rounded = Math.round(suggested / 5) * 5;
  const delta = rounded - avgWeight;

  return {
    weight: rounded,
    delta: Math.round(delta * 10) / 10,
  };
};

/* =========================
       PROGRESSIVE EXERCISE VARIANTS
       ========================= */

export const getExerciseVariant = (exercise, week, difficulty) => {
  const phase = Math.min(2, Math.floor((week - 1) / 3));

  let name = exercise.name;
  let notes = exercise.notes || "";

  const lowerName = exercise.name.toLowerCase();
  const isBigLift =
    /squat|deadlift|bench press|hip thrust|row|press|pull-up|chin-up/.test(
      lowerName
    );
  const isBodyweight =
    /push-up|pull-up|chin-up|dip|inverted row|sit-up|plank|jump/.test(
      lowerName
    );

  // Beginner: emphasize easier variants + technique
  if (difficulty === "beginner") {
    if (isBigLift && phase === 0) {
      notes =
        notes ||
        "Use lighter loads, focus on perfect technique before adding weight.";
    }
    if (isBodyweight && phase === 0) {
      notes =
        notes ||
        "Use easier variations (incline, assisted, bands) and own your form.";
    }
    return { name, notes };
  }

  // Intermediate: add tempo / top set focus later in block
  if (difficulty === "intermediate") {
    if (isBigLift && phase === 1) {
      notes =
        notes ||
        "Use a controlled 2s eccentric and focus on tight, repeatable reps.";
    } else if (isBigLift && phase === 2) {
      notes =
        notes ||
        "Work up to a hard top set (RPE ~8), then do 1–2 lighter back-off sets.";
    }
    if (isBodyweight && phase >= 1) {
      notes =
        notes ||
        "Make reps cleaner, add slight tempo or reduced rest to increase difficulty.";
    }
    return { name, notes };
  }

  // Advanced: add pause reps / top-set + backoffs / weighted calisthenics
  if (difficulty === "advanced") {
    if (isBigLift) {
      if (phase === 1) {
        if (!/pause/i.test(name)) {
          name = `${name} – Pause Reps`;
        }
        notes =
          "Use 1–2 second pauses in the hardest part of the range. Control every rep.";
      } else if (phase === 2) {
        if (!/top set/i.test(name.toLowerCase())) {
          name = `${name} – Top Set + Backoffs`;
        }
        notes =
          "Work up to a heavy top set (RPE 8–9), then 2–3 back-off sets at ~85–90% of that weight.";
      }
    }

    if (isBodyweight) {
      if (/push-up/.test(lowerName) && phase >= 1) {
        if (!/weighted/i.test(lowerName)) {
          name = name.replace(/Push-Ups?/i, "Weighted Push-Ups");
        }
        notes =
          "Add weight (vest, plates, bands) or use deficit/tempo to keep sets challenging at target reps.";
      } else if (/pull-up|chin-up/.test(lowerName) && phase >= 1) {
        if (!/weighted/i.test(lowerName)) {
          name = name.replace(/Pull-Ups?/i, "Weighted Pull-Ups");
        }
        notes =
          "Add weight via belt or hold a DB. Hit clean, explosive reps and control the negative.";
      } else if (/dip/.test(lowerName) && phase >= 1) {
        if (!/weighted/i.test(lowerName)) {
          name = name.replace(/Dips?/i, "Weighted Dips");
        }
        notes =
          "Add load while keeping shoulders healthy—no ego dipping, full ROM.";
      }
    }

    return { name, notes };
  }

  return { name, notes };
};

/* =========================
       HERO LIST (NO MISC UNIVERSE)
       ========================= */

export const HEROES = [
  // === MARVEL ===
  {
    id: "cap",
    name: "Captain America",
    universe: "Marvel",
    programType: "super_soldier",
    gender: "male",
    physique:
      "Athletic, muscular, battle-ready. Strong chest, shoulders, and athletic legs.",
  },
  {
    id: "thor",
    name: "Thor",
    universe: "Marvel",
    programType: "powerhouse",
    gender: "male",
    physique:
      "Godlike powerhouse. Dense mass, huge arms, big shoulders, thick back.",
  },
  {
    id: "hulk",
    name: "Hulk",
    universe: "Marvel",
    programType: "classic_bodybuilder",
    gender: "male",
    physique:
      "Massive raw strength. Think total body mass, boulder shoulders, gorilla arms.",
  },
  {
    id: "wolverine",
    name: "Wolverine",
    universe: "Marvel",
    programType: "combat_athlete",
    gender: "male",
    physique:
      "Compact, dense muscle. Thick forearms, wide back, low bodyfat. Short-king tank.",
  },
  {
    id: "spiderman",
    name: "Spider-Man",
    universe: "Marvel",
    programType: "speedster",
    gender: "male",
    physique:
      "Lean, agile, functional muscle. Defined arms, strong core, wiry athlete.",
  },
  {
    id: "blackpanther",
    name: "Black Panther",
    universe: "Marvel",
    programType: "combat_athlete",
    gender: "male",
    physique: "Athletic, functional power. Speed, agility, lean muscle.",
  },
  {
    id: "ironman",
    name: "Iron Man",
    universe: "Marvel",
    programType: "classic_bodybuilder",
    gender: "male",
    physique:
      "Balanced, aesthetic physique. Strong chest, defined arms, lean muscle.",
  },
  {
    id: "thanos",
    name: "Thanos",
    universe: "Marvel",
    programType: "classic_bodybuilder",
    gender: "male",
    physique: "Massive bodybuilder. Thick everywhere, intimidating size.",
  },
  {
    id: "captainmarvel",
    name: "Captain Marvel",
    universe: "Marvel",
    programType: "combat_athlete",
    gender: "female",
    physique:
      "Powerful, explosive physique. Strong upper body, athletic legs, combat-ready conditioning.",
  },
  {
    id: "blackwidow",
    name: "Black Widow",
    universe: "Marvel",
    programType: "agile_hero",
    gender: "female",
    physique:
      "Lean, athletic, highly conditioned. Strength balanced with flexibility and combat agility.",
  },

  // === DC ===
  {
    id: "superman",
    name: "Superman",
    universe: "DC",
    programType: "classic_bodybuilder",
    gender: "male",
    physique:
      "Classic heroic physique. Wide shoulders, thick chest, powerful legs.",
  },
  {
    id: "batman",
    name: "Batman",
    universe: "DC",
    programType: "combat_athlete",
    gender: "male",
    physique:
      "Peak human. Strong, lean, functional. Combat athlete aesthetics.",
  },
  {
    id: "flash",
    name: "The Flash",
    universe: "DC",
    programType: "speedster",
    gender: "male",
    physique: "Lean speedster. Low bodyfat, defined legs, runner’s build.",
  },
  {
    id: "aquaman",
    name: "Aquaman",
    universe: "DC",
    programType: "super_soldier",
    gender: "male",
    physique: "Swimming power. Big lats, strong legs, rugged and athletic.",
  },
  {
    id: "wonderwoman",
    name: "Wonder Woman",
    universe: "DC",
    programType: "amazonian",
    gender: "female",
    physique:
      "Athletic warrior. Strong legs, core, shoulders. Powerful and graceful.",
  },
  {
    id: "darkseid",
    name: "Darkseid",
    universe: "DC",
    programType: "classic_bodybuilder",
    gender: "male",
    physique: "Godlike mass. Maximum size, imposing presence, dense muscle.",
  },
  {
    id: "supergirl",
    name: "Supergirl",
    universe: "DC",
    programType: "combat_athlete",
    gender: "female",
    physique:
      "Heroic physique with balanced power and athleticism. Strong, fast, and explosive.",
  },
  {
    id: "batgirl",
    name: "Batgirl",
    universe: "DC",
    programType: "speedster",
    gender: "female",
    physique:
      "Agile, acrobatic, and tactical. Lean muscle, strong core, and high-output conditioning.",
  },

  // === ANIME ===
  {
    id: "goku",
    name: "Goku",
    universe: "Anime",
    programType: "combat_athlete",
    gender: "male",
    physique:
      "Athletic warrior. Balanced muscle, explosive power, combat ready.",
  },
  {
    id: "vegeta",
    name: "Vegeta",
    universe: "Anime",
    programType: "classic_bodybuilder",
    gender: "male",
    physique:
      "Compact warrior build. Strong arms, defined core, aggressive energy.",
  },
  {
    id: "saitama",
    name: "Saitama",
    universe: "Anime",
    programType: "one_punch",
    gender: "male",
    physique: "Deceptively simple. Lean, balanced, ridiculous engine.",
  },
  {
    id: "levi",
    name: "Levi Ackerman",
    universe: "Anime",
    programType: "speedster",
    gender: "male",
    physique:
      "Compact and deadly. Lean muscle, explosive speed, wiry strength.",
  },
  {
    id: "allMight",
    name: "All Might",
    universe: "Anime",
    programType: "classic_bodybuilder",
    gender: "male",
    physique:
      "Symbol of peace. Massive heroic build, huge chest and shoulders.",
  },

  // === GAMES ===
  {
    id: "kratos",
    name: "Kratos",
    universe: "Games",
    programType: "powerhouse",
    gender: "male",
    physique: "War god. Massive arms, thick core, ruthless presence.",
  },
  {
    id: "geralt",
    name: "Geralt of Rivia",
    universe: "Games",
    programType: "combat_athlete",
    gender: "male",
    physique:
      "Functional witcher. Athletic, balanced strength, combat endurance.",
  },
  {
    id: "masterchief",
    name: "Master Chief",
    universe: "Games",
    programType: "super_soldier",
    gender: "male",
    physique: "Super soldier. Athletic, powerful, built for war.",
  },
  {
    id: "laracroft",
    name: "Lara Croft",
    universe: "Games",
    programType: "amazonian",
    gender: "female",
    physique:
      "Athletic adventurer. Lean, agile, capable of climbing and combat.",
  },
  {
    id: "doom",
    name: "Doom Slayer",
    universe: "Games",
    programType: "classic_bodybuilder",
    gender: "male",
    physique:
      "Demon-slaying mass. Thick arms, armored torso, unstoppable power.",
  },
  {
    id: "kazuya",
    name: "Kazuya Mishima",
    universe: "Games",
    programType: "combat_athlete",
    gender: "male",
    physique:
      "Fighting physique. Dense muscle, strong legs, martial arts build.",
  },
];

/* =========================
       EXPORTS
       ========================= */

export // already exported inline: resolveProgram, getProgressiveSetsReps,
// getSetsCountFromPrescription, getSuggestedWeight, getExerciseVariant,
// HEROES
 {};
